import React, { useState, useEffect } from 'react';
import { Plus, Edit2, Trash2, ExternalLink, Github, Calendar, Code, X, Save, AlertCircle, LogOut, Database, Info } from 'lucide-react';
import { useAuth } from '../context/authContext';
import { useNavigate } from 'react-router-dom';

// Type definitions
interface Project {
  id: string;
  title: string;
  description: string;
  technologies: string[];
  githubURL: string;
  websiteURL: string;
  dateCreated: any; // Firestore timestamp or Date
  collection: string;
  features?: string[];
  content?: string;
}

interface ProjectFormData {
  title: string;
  description: string;
  technologies: string[];
  githubURL: string;
  websiteURL: string;
  collection: string;
  features: string[];
  content: string;
}

const AdminDashboard: React.FC = () => {
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [editingProject, setEditingProject] = useState<Project | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<Project | null>(null);
  const [formData, setFormData] = useState<ProjectFormData>({
    title: '',
    description: '',
    technologies: [],
    githubURL: '',
    websiteURL: '',
    collection: 'software',
    features: [],
    content: ''
  });
  const [techInput, setTechInput] = useState<string>('');
  const [featureInput, setFeatureInput] = useState<string>('');
  const [error, setError] = useState<string>('');
  const [saving, setSaving] = useState<boolean>(false);
  const [loggingOut, setLoggingOut] = useState<boolean>(false);

  const { 
    currentUser, 
    projects, 
    projectsLoading,
    loading: authLoading,
    createProject,
    updateProject,
    deleteProject,
    logout,
    collections,
    getProjectsByCollection
  } = useAuth();

  const navigate = useNavigate();

  // Route protection - redirect if not authenticated
  useEffect(() => {
    if (!authLoading && !currentUser) {
      navigate('/', { replace: true });
    }
  }, [currentUser, authLoading, navigate]);

  // Handle logout
  const handleLogout = async (): Promise<void> => {
    try {
      setLoggingOut(true);
      await logout();
      navigate('/', { replace: true });
    } catch (error) {
      console.error('Error logging out:', error);
      setError('Failed to log out. Please try again.');
    } finally {
      setLoggingOut(false);
    }
  };

  // Reset form
  const resetForm = (): void => {
    setFormData({
      title: '',
      description: '',
      technologies: [],
      githubURL: '',
      websiteURL: '',
      collection: 'software',
      features: [],
      content: ''
    });
    setTechInput('');
    setFeatureInput('');
    setEditingProject(null);
    setError('');
  };

  // Open modal for creating new project
  const openCreateModal = (): void => {
    resetForm();
    setIsModalOpen(true);
  };

  // Open modal for editing project
  const openEditModal = (project: Project): void => {
    setFormData({
      title: project.title,
      description: project.description,
      technologies: project.technologies || [],
      githubURL: project.githubURL || '',
      websiteURL: project.websiteURL || '',
      collection: project.collection || 'software',
      features: project.features || [],
      content: project.content || ''
    });
    setEditingProject(project);
    setIsModalOpen(true);
  };

  // Close modal
  const closeModal = (): void => {
    setIsModalOpen(false);
    resetForm();
  };

  // Add technology to list
  const addTechnology = (): void => {
    if (techInput.trim() && !formData.technologies.includes(techInput.trim())) {
      setFormData({
        ...formData,
        technologies: [...formData.technologies, techInput.trim()]
      });
      setTechInput('');
    }
  };

  // Remove technology from list
  const removeTechnology = (tech: string): void => {
    setFormData({
      ...formData,
      technologies: formData.technologies.filter(t => t !== tech)
    });
  };

  // Add feature to list
  const addFeature = (): void => {
    if (featureInput.trim() && !formData.features.includes(featureInput.trim())) {
      setFormData({
        ...formData,
        features: [...formData.features, featureInput.trim()]
      });
      setFeatureInput('');
    }
  };

  // Remove feature from list
  const removeFeature = (feature: string): void => {
    setFormData({
      ...formData,
      features: formData.features.filter(f => f !== feature)
    });
  };

  // Handle form submission
  const handleSubmit = async (): Promise<void> => {
    if (!formData.title.trim()) {
      setError('Project title is required');
      return;
    }

    try {
      setSaving(true);
      setError('');

      if (editingProject) {
        // Update existing project
        await updateProject(editingProject.id, formData);
      } else {
        // Create new project
        await createProject(formData);
      }

      closeModal();
    } catch (error: unknown) {
      console.error('Error saving project:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to save project. Please try again.';
      setError(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // Delete project
  const handleDelete = async (projectId: string): Promise<void> => {
    try {
      await deleteProject(projectId);
      setDeleteConfirm(null);
    } catch (error: unknown) {
      console.error('Error deleting project:', error);
      const errorMessage = error instanceof Error ? error.message : 'Failed to delete project';
      setError(errorMessage);
    }
  };

  const formatDate = (timestamp: any): string => {
    if (!timestamp) return 'Unknown';
    try {
      // Handle both Firestore timestamps and regular dates
      const date = timestamp.toDate ? timestamp.toDate() : new Date(timestamp);
      return date.toLocaleDateString();
    } catch {
      return 'Unknown';
    }
  };

  const getCollectionDisplayName = (collection: string): string => {
    switch (collection) {
      case 'software':
        return 'Software';
      case 'aiprojects':
        return 'AI Projects';
      default:
        return collection;
    }
  };

  const getCollectionColor = (collection: string): string => {
    switch (collection) {
      case 'software':
        return 'bg-green-600/20 text-green-400';
      case 'aiprojects':
        return 'bg-purple-600/20 text-purple-400';
      default:
        return 'bg-gray-600/20 text-gray-400';
    }
  };

  // Get project counts by collection
  const softwareProjects = getProjectsByCollection('software');
  const aiProjects = getProjectsByCollection('aiprojects');

  // Show loading while checking authentication
  if (authLoading) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
          <p className="text-gray-400">Loading...</p>
        </div>
      </div>
    );
  }

  // Show access denied if not authenticated
  if (!currentUser) {
    return (
      <div className="min-h-screen bg-gray-900 flex items-center justify-center">
        <div className="text-white text-center">
          <AlertCircle className="w-16 h-16 mx-auto mb-4 text-red-400" />
          <h2 className="text-2xl font-bold mb-2">Access Denied</h2>
          <p className="text-gray-400">You need to be logged in to access the dashboard.</p>
          <button
            onClick={() => navigate('/')}
            className="mt-4 bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors"
          >
            Go Home
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 text-white p-4 sm:p-6 pt-20 sm:pt-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl font-bold mb-2">Project Dashboard</h1>
            <p className="text-gray-400">Manage your projects and showcase your work</p>
            {projects && projects.length > 0 && (
              <div className="flex flex-wrap items-center gap-4 mt-2 text-sm">
                <p className="text-blue-400">{projects.length} projects total</p>
                <p className="text-green-400">{softwareProjects.length} software</p>
                <p className="text-purple-400">{aiProjects.length} AI projects</p>
              </div>
            )}
          </div>
          <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
            <button
              onClick={openCreateModal}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-3 sm:py-2 rounded-lg flex items-center justify-center gap-2 transition-colors font-medium"
            >
              <Plus className="w-5 h-5" />
              New Project
            </button>
            <button
              onClick={handleLogout}
              disabled={loggingOut}
              className="bg-red-600 hover:bg-red-700 disabled:opacity-50 px-4 py-3 sm:py-2 rounded-lg flex items-center justify-center gap-2 transition-colors font-medium"
              title="Logout"
            >
              {loggingOut ? (
                <div className="w-5 h-5 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
              ) : (
                <LogOut className="w-5 h-5" />
              )}
              Logout
            </button>
          </div>
        </div>
      </div>

      {/* Error Display */}
      {error && (
        <div className="max-w-7xl mx-auto mb-6">
          <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-3 flex items-center gap-2">
            <AlertCircle className="w-4 h-4 text-red-400 flex-shrink-0" />
            <span className="text-red-300 text-sm flex-1">{error}</span>
            <button onClick={() => setError('')} className="text-red-400 hover:text-red-300 flex-shrink-0">
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>
      )}

      {/* Projects Grid */}
      <div className="max-w-7xl mx-auto">
        {projectsLoading ? (
          <div className="flex items-center justify-center py-12">
            <div className="w-8 h-8 border-2 border-blue-500 border-t-transparent rounded-full animate-spin"></div>
          </div>
        ) : !projects || projects.length === 0 ? (
          <div className="text-center py-12">
            <Code className="w-16 h-16 mx-auto mb-4 text-gray-500" />
            <h3 className="text-xl font-semibold mb-2 text-gray-400">No projects yet</h3>
            <p className="text-gray-500 mb-4">Create your first project to get started</p>
            <button
              onClick={openCreateModal}
              className="bg-blue-600 hover:bg-blue-700 px-4 py-2 rounded-lg transition-colors"
            >
              Create Project
            </button>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {projects.map((project: Project) => (
              <div key={project.id} className="bg-gray-800 border border-gray-700 rounded-lg p-6 hover:border-gray-600 transition-colors">
                {/* Project Header */}
                <div className="flex items-start justify-between mb-4">
                  <div className="flex-1 min-w-0">
                    <h3 className="text-xl font-semibold mb-2 truncate">{project.title}</h3>
                    <div className="flex items-center gap-2 mb-2 flex-wrap">
                      <span className={`inline-flex items-center gap-1 px-2 py-1 text-xs rounded-full ${getCollectionColor(project.collection)}`}>
                        <Database className="w-3 h-3" />
                        {getCollectionDisplayName(project.collection)}
                      </span>
                    </div>
                  </div>
                  <div className="flex gap-1 ml-4 flex-shrink-0">
                    <button
                      onClick={() => openEditModal(project)}
                      className="p-2 text-gray-400 hover:text-blue-400 hover:bg-blue-600/10 rounded-lg transition-colors"
                      title="Edit project"
                    >
                      <Edit2 className="w-4 h-4" />
                    </button>
                    <button
                      onClick={() => setDeleteConfirm(project)}
                      className="p-2 text-gray-400 hover:text-red-400 hover:bg-red-600/10 rounded-lg transition-colors"
                      title="Delete project"
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>

                {/* Description */}
                <p className="text-gray-400 mb-4 line-clamp-3 text-sm">{project.description}</p>

                {/* Technologies */}
                {project.technologies && project.technologies.length > 0 && (
                  <div className="mb-4">
                    <div className="flex flex-wrap gap-1">
                      {project.technologies.slice(0, 4).map((tech: string, index: number) => (
                        <span key={index} className="px-2 py-1 bg-gray-700 text-gray-300 text-xs rounded">
                          {tech}
                        </span>
                      ))}
                      {project.technologies.length > 4 && (
                        <span className="px-2 py-1 bg-gray-600 text-gray-400 text-xs rounded">
                          +{project.technologies.length - 4}
                        </span>
                      )}
                    </div>
                  </div>
                )}

                {/* Links and Date */}
                <div className="flex items-center justify-between text-sm text-gray-500">
                  <div className="flex items-center gap-1">
                    <Calendar className="w-4 h-4" />
                    <span className="text-xs">{formatDate(project.dateCreated)}</span>
                  </div>
                  <div className="flex gap-2">
                    {project.githubURL && (
                      <a
                        href={project.githubURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 hover:text-white hover:bg-gray-700 rounded transition-all duration-200"
                        title="View on GitHub"
                      >
                        <Github className="w-4 h-4" />
                      </a>
                    )}
                    {project.websiteURL && (
                      <a
                        href={project.websiteURL}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="p-1.5 hover:text-white hover:bg-gray-700 rounded transition-all duration-200"
                        title="Visit website"
                      >
                        <ExternalLink className="w-4 h-4" />
                      </a>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Create/Edit Modal */}
      {isModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-start sm:items-center justify-center p-4 overflow-y-auto">
          <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-2xl my-8 sm:my-0">
            {/* Modal Header */}
            <div className="flex items-center justify-between p-4 sm:p-6 border-b border-gray-700">
              <h2 className="text-lg sm:text-xl font-semibold">
                {editingProject ? 'Edit Project' : 'Create New Project'}
              </h2>
              <button onClick={closeModal} className="text-gray-400 hover:text-white">
                <X className="w-6 h-6" />
              </button>
            </div>

            {/* Modal Content */}
            <div className="p-4 sm:p-6 space-y-4 max-h-[80vh] overflow-y-auto">
              {error && (
                <div className="bg-red-900/30 border border-red-500/30 rounded-lg p-3 flex items-start gap-2">
                  <AlertCircle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                  <span className="text-red-300 text-sm">{error}</span>
                </div>
              )}

              {/* Collection */}
              <div>
                <label className="block text-sm font-medium mb-2">Collection *</label>
                <select
                  value={formData.collection}
                  onChange={(e) => setFormData({...formData, collection: e.target.value})}
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg focus:border-blue-400 focus:outline-none"
                >
                  <option value="software">Software Projects</option>
                  <option value="aiprojects">AI Projects</option>
                </select>
                <p className="text-xs text-gray-500 mt-1">
                  Choose which collection to store this project in
                </p>
              </div>

              {/* Title */}
              <div>
                <label className="block text-sm font-medium mb-2">Project Title *</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({...formData, title: e.target.value})}
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg focus:border-blue-400 focus:outline-none"
                  placeholder="Enter project title"
                />
              </div>

              {/* Description */}
              <div>
                <label className="block text-sm font-medium mb-2">Description</label>
                <textarea
                  value={formData.description}
                  onChange={(e) => setFormData({...formData, description: e.target.value})}
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg focus:border-blue-400 focus:outline-none h-24 resize-none"
                  placeholder="Describe your project (supports markdown syntax)"
                />
                <div className="flex items-start gap-2 mt-2 p-3 bg-blue-900/20 border border-blue-500/20 rounded-lg">
                  <Info className="w-4 h-4 text-blue-400 mt-0.5 flex-shrink-0" />
                  <div className="text-xs text-blue-300">
                    <p className="font-medium mb-1">Markdown supported:</p>
                    <p className="text-blue-400/80">Use # for headings, **bold**, *italic*, - for lists</p>
                  </div>
                </div>
              </div>

              {/* Technologies */}
              <div>
                <label className="block text-sm font-medium mb-2">Technologies</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={techInput}
                    onChange={(e) => setTechInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addTechnology();
                      }
                    }}
                    className="flex-1 p-3 bg-gray-900 border border-gray-600 rounded-lg focus:border-blue-400 focus:outline-none text-sm"
                    placeholder="Add technology (press Enter)"
                  />
                  <button
                    type="button"
                    onClick={addTechnology}
                    className="px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-sm font-medium"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.technologies.map((tech: string, index: number) => (
                    <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-700 rounded text-sm">
                      {tech}
                      <button
                        type="button"
                        onClick={() => removeTechnology(tech)}
                        className="text-gray-400 hover:text-white"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Features */}
              <div>
                <label className="block text-sm font-medium mb-2">Key Features</label>
                <div className="flex gap-2 mb-2">
                  <input
                    type="text"
                    value={featureInput}
                    onChange={(e) => setFeatureInput(e.target.value)}
                    onKeyPress={(e) => {
                      if (e.key === 'Enter') {
                        e.preventDefault();
                        addFeature();
                      }
                    }}
                    className="flex-1 p-3 bg-gray-900 border border-gray-600 rounded-lg focus:border-blue-400 focus:outline-none text-sm"
                    placeholder="Add feature (press Enter)"
                  />
                  <button
                    type="button"
                    onClick={addFeature}
                    className="px-4 py-3 bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors text-sm font-medium"
                  >
                    Add
                  </button>
                </div>
                <div className="flex flex-wrap gap-2">
                  {formData.features.map((feature: string, index: number) => (
                    <span key={index} className="inline-flex items-center gap-1 px-2 py-1 bg-gray-700 rounded text-sm">
                      {feature}
                      <button
                        type="button"
                        onClick={() => removeFeature(feature)}
                        className="text-gray-400 hover:text-white"
                      >
                        <X className="w-3 h-3" />
                      </button>
                    </span>
                  ))}
                </div>
              </div>

              {/* Content */}
              <div>
                <label className="block text-sm font-medium mb-2">Additional Details</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({...formData, content: e.target.value})}
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg focus:border-blue-400 focus:outline-none h-24 resize-none"
                  placeholder="Additional project details, challenges, learnings, etc. (supports markdown)"
                />
                <p className="text-xs text-gray-500 mt-1">
                  Supports markdown syntax for rich formatting
                </p>
              </div>

              {/* GitHub URL */}
              <div>
                <label className="block text-sm font-medium mb-2">GitHub URL</label>
                <input
                  type="url"
                  value={formData.githubURL}
                  onChange={(e) => setFormData({...formData, githubURL: e.target.value})}
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg focus:border-blue-400 focus:outline-none text-sm"
                  placeholder="https://github.com/username/repo"
                />
              </div>

              {/* Website URL */}
              <div>
                <label className="block text-sm font-medium mb-2">Website URL</label>
                <input
                  type="url"
                  value={formData.websiteURL}
                  onChange={(e) => setFormData({...formData, websiteURL: e.target.value})}
                  className="w-full p-3 bg-gray-900 border border-gray-600 rounded-lg focus:border-blue-400 focus:outline-none text-sm"
                  placeholder="https://your-project-website.com"
                />
              </div>

              {/* Submit Button */}
              <div className="flex flex-col sm:flex-row gap-3 pt-4">
                <button
                  type="button"
                  onClick={handleSubmit}
                  disabled={saving}
                  className="flex-1 bg-blue-600 hover:bg-blue-700 disabled:opacity-50 px-4 py-3 rounded-lg flex items-center justify-center gap-2 transition-colors font-medium"
                >
                  {saving ? (
                    <>
                      <div className="w-4 h-4 border-2 border-white border-t-transparent rounded-full animate-spin"></div>
                      Saving...
                    </>
                  ) : (
                    <>
                      <Save className="w-4 h-4" />
                      {editingProject ? 'Update Project' : 'Create Project'}
                    </>
                  )}
                </button>
                <button
                  type="button"
                  onClick={closeModal}
                  className="px-4 py-3 bg-gray-700 hover:bg-gray-600 rounded-lg transition-colors font-medium sm:min-w-[100px]"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirmation Modal */}
      {deleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
          <div className="bg-gray-800 border border-gray-700 rounded-lg w-full max-w-md">
            <div className="p-6">
              <div className="flex items-center gap-3 mb-4">
                <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0" />
                <h3 className="text-lg font-semibold">Delete Project</h3>
              </div>
              <p className="text-gray-400 mb-2">
                Are you sure you want to delete "<strong>{deleteConfirm.title}</strong>"?
              </p>
              <p className="text-sm text-gray-500 mb-6">
                This will permanently remove it from the <strong>{getCollectionDisplayName(deleteConfirm.collection)}</strong> collection. This action cannot be undone.
              </p>
              <div className="flex flex-col sm:flex-row gap-3">
                <button
                  onClick={() => handleDelete(deleteConfirm.id)}
                  className="flex-1 bg-red-600 hover:bg-red-700 px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  Delete
                </button>
                <button
                  onClick={() => setDeleteConfirm(null)}
                  className="flex-1 bg-gray-700 hover:bg-gray-600 px-4 py-2 rounded-lg transition-colors font-medium"
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;